/**
 * `/internal/ops-users/*` contract types (ADR-004). Epic #109 P3.18.
 *
 * Ops-admin surface for managing ops user accounts: list, create,
 * update roles/Feishu binding, disable, enable, reset password, and
 * revoke live sessions.
 */

import type { BrandId } from './brands.ts';
import type { InviteCode } from './internal-invite-codes.ts';

export const ROLE_OPS_SUPER_ADMIN = 'ops-super-admin';
export const ROLE_OPS_ADMIN = 'ops-admin';
export const ROLE_OPS_SUPPORT = 'ops-support';
export const ROLE_OPS_SALES_LEAD = 'ops-sales-lead';
export const ROLE_OPS_CUSTOMER_MANAGER = 'ops-customer-manager';
export const ROLE_OPS_FINANCE_MANAGER = 'ops-finance-manager';
export const ROLE_OPS_SUPPLY_DEMAND_MANAGER = 'ops-supply-demand-manager';
export const ROLE_OPS_RUNTIME_OPERATOR = 'ops-runtime-operator';

export const OPS_ROLE_DISPLAY_ORDER = [
  ROLE_OPS_SUPER_ADMIN,
  ROLE_OPS_ADMIN,
  ROLE_OPS_SUPPORT,
  ROLE_OPS_SALES_LEAD,
  ROLE_OPS_CUSTOMER_MANAGER,
  ROLE_OPS_FINANCE_MANAGER,
  ROLE_OPS_SUPPLY_DEMAND_MANAGER,
  ROLE_OPS_RUNTIME_OPERATOR,
] as const;
export type OpsPermissionRole = (typeof OPS_ROLE_DISPLAY_ORDER)[number];

/**
 * Roles that ordinary ops-admin user-management flows may grant.
 *
 * `ops-super-admin` is intentionally not assignable through the generic
 * ops-users API/UI. It is a bootstrap/break-glass role whose assignment
 * should stay out-of-band until a dedicated elevated approval flow exists.
 */
export const ASSIGNABLE_OPS_ROLES = [
  ROLE_OPS_ADMIN,
  ROLE_OPS_SUPPORT,
  ROLE_OPS_SALES_LEAD,
  ROLE_OPS_CUSTOMER_MANAGER,
  ROLE_OPS_FINANCE_MANAGER,
  ROLE_OPS_SUPPLY_DEMAND_MANAGER,
  ROLE_OPS_RUNTIME_OPERATOR,
] as const;
export type AssignableOpsRole = (typeof ASSIGNABLE_OPS_ROLES)[number];

export function expandOpsRoles(rawRoles: readonly string[]): string[] {
  const roleSet = new Set(rawRoles.filter((role) => role.length > 0));
  if (roleSet.has(ROLE_OPS_SUPER_ADMIN)) {
    roleSet.add(ROLE_OPS_ADMIN);
    roleSet.add(ROLE_OPS_SUPPORT);
    roleSet.add(ROLE_OPS_SALES_LEAD);
    roleSet.add(ROLE_OPS_CUSTOMER_MANAGER);
    roleSet.add(ROLE_OPS_FINANCE_MANAGER);
    roleSet.add(ROLE_OPS_SUPPLY_DEMAND_MANAGER);
    roleSet.add(ROLE_OPS_RUNTIME_OPERATOR);
  }
  if (roleSet.has(ROLE_OPS_ADMIN)) {
    roleSet.add(ROLE_OPS_SUPPORT);
  }
  // Epic #1030 (sales system): a sales lead can do everything a customer
  // manager can, plus approve/configure — mirror the admin ⊃ support edge.
  if (roleSet.has(ROLE_OPS_SALES_LEAD)) {
    roleSet.add(ROLE_OPS_CUSTOMER_MANAGER);
  }

  const expanded: string[] = [];
  for (const role of OPS_ROLE_DISPLAY_ORDER) {
    if (roleSet.has(role)) {
      expanded.push(role);
      roleSet.delete(role);
    }
  }
  for (const role of rawRoles) {
    if (roleSet.has(role)) {
      expanded.push(role);
      roleSet.delete(role);
    }
  }
  return expanded;
}

export function hasAnyOpsRole(
  rawRoles: readonly string[],
  requiredRoles: readonly string[],
): boolean {
  if (requiredRoles.length === 0) {
    return true;
  }
  const effective = new Set(expandOpsRoles(rawRoles));
  return requiredRoles.some((role) => effective.has(role));
}

/**
 * Lower number is higher privilege. Rank is the earliest role in
 * {@link OPS_ROLE_DISPLAY_ORDER} after expansion, so `ops-super-admin`
 * outranks `ops-admin`, and `ops-admin` outranks every assignable role.
 * Unknown-only role sets rank after the catalog (least privilege).
 */
export function opsRolePrivilegeRank(roles: readonly string[]): number {
  const effective = new Set(expandOpsRoles(roles));
  for (let index = 0; index < OPS_ROLE_DISPLAY_ORDER.length; index += 1) {
    const role = OPS_ROLE_DISPLAY_ORDER[index];
    if (role !== undefined && effective.has(role)) {
      return index;
    }
  }
  return OPS_ROLE_DISPLAY_ORDER.length;
}

/**
 * Rank gate for the **non-production** direct password-set path
 * (router#2032). Production never calls this: it only invites, and the
 * token is mailed to the email stored on the account.
 *
 * An operator may set a password only for a user whose privilege rank
 * is greater than or equal to the operator's own. Strictly higher
 * target rank is refused so this write cannot mint an
 * `ops-super-admin` password. Equal rank is a property of the rank
 * helper, not a production write face — production blocks the endpoint
 * before rank is consulted.
 */
export function canSetOpsUserPassword(
  actorRoles: readonly string[],
  targetRoles: readonly string[],
): boolean {
  return opsRolePrivilegeRank(actorRoles) <= opsRolePrivilegeRank(targetRoles);
}

/** Same bounds as tenant password create/reset. */
export const OPS_PASSWORD_MIN_LENGTH = 10;
export const OPS_PASSWORD_MAX_LENGTH = 128;

/** One-hour invite token, matching tenant password-reset TTL. */
export const OPS_PASSWORD_INVITE_TTL_MS = 60 * 60 * 1000;

/**
 * Ways an ops user can sign in to ops-console, in display order. Surfaced
 * next to a display name so two accounts with the same name (a Feishu SSO
 * account and an email+password account) can be told apart in pickers.
 */
export const OPS_LOGIN_METHODS = ['feishu', 'password', 'github'] as const;
export type OpsLoginMethod = (typeof OPS_LOGIN_METHODS)[number];

/** Domain of the synthetic `ops_users.email` minted for Feishu-only accounts. */
export const OPS_SYNTHETIC_EMAIL_DOMAIN = 'ops.internal';

export function opsUserLoginMethods(flags: {
  readonly feishu: boolean;
  readonly password: boolean;
  readonly github: boolean;
}): OpsLoginMethod[] {
  return OPS_LOGIN_METHODS.filter((method) => flags[method]);
}

/**
 * Human-readable email for an ops user: the stored email unless it is the
 * synthetic `@ops.internal` placeholder, in which case the Feishu enterprise
 * email (when bound), else `null`. Blank values count as missing.
 */
export function opsUserContactEmail(
  email: string | null,
  feishuEnterpriseEmail: string | null,
): string | null {
  const trimmedEmail = email?.trim() ?? '';
  const syntheticSuffix = `@${OPS_SYNTHETIC_EMAIL_DOMAIN}`.toLowerCase();
  if (trimmedEmail.length > 0 && !trimmedEmail.toLowerCase().endsWith(syntheticSuffix)) {
    return trimmedEmail;
  }
  const trimmedFeishuEnterpriseEmail = feishuEnterpriseEmail?.trim() ?? '';
  if (trimmedFeishuEnterpriseEmail.length > 0) {
    return trimmedFeishuEnterpriseEmail;
  }
  return null;
}

export interface OpsUserGithubIdentity {
  github_user_id: string;
  github_login: string;
  github_avatar_url: string | null;
}

export interface OpsUserFeishuIdentity {
  feishu_tenant_key: string;
  feishu_open_id: string;
  feishu_union_id: string | null;
  feishu_user_id: string | null;
  feishu_name: string | null;
  feishu_avatar_url: string | null;
  feishu_enterprise_email: string | null;
}

export interface OpsUserRecord {
  id: string;
  email: string;
  /**
   * Operator-facing identity label, resolved by platform-api. The stored
   * `ops_users.display_name` column wins when non-empty (0062); otherwise it
   * falls back to the freshest safe profile field, in the order
   * feishu_name → feishu_enterprise_email → feishu_open_id → github_login →
   * email. The column exists because a salesperson provisioned with email +
   * password has no Feishu or GitHub binding to derive a human name from.
   * Never empty: `email` is the terminal fallback.
   */
  display_name: string;
  /**
   * The raw stored override, `null` when the label above is derived. Exposed
   * separately so the console can show an edit field that is empty rather
   * than pre-filled with a derived value it would then persist.
   */
  display_name_override: string | null;
  created_at: string;
  disabled_at: string | null;
  github_user_id: string | null;
  github_login: string | null;
  github_avatar_url: string | null;
  github_linked_at: string | null;
  github_last_login_at: string | null;
  feishu_tenant_key: string | null;
  feishu_open_id: string | null;
  feishu_union_id: string | null;
  feishu_user_id: string | null;
  feishu_name: string | null;
  feishu_avatar_url: string | null;
  feishu_enterprise_email: string | null;
  feishu_linked_at: string | null;
  feishu_last_login_at: string | null;
  session_revoked_before: string | null;
  /**
   * Whitelist of roles assigned to this user (JSON array in the DB).
   * Treated as opaque strings; role semantics live in ops-api
   * `requireRoles`.
   */
  roles: string[];
  /**
   * `true` iff an ops_password_credentials row exists for this user.
   * `false` when no password has been seeded — either the account was
   * "reset" (credential deleted) or it only ever signed in via Feishu SSO,
   * an independent login path that never seeds a password. Password-less is
   * therefore not on its own a login blocker: a Feishu-bound account can
   * still authenticate. An account is only locked out when it has neither a
   * password nor a Feishu binding.
   */
  has_password: boolean;
}

export interface ListOpsUsersResponse {
  users: OpsUserRecord[];
  /**
   * True only in non-production environments where the operator-facing
   * direct set-password write is armed. Production is always false;
   * operators invite instead. The API does not echo the invite token;
   * it is mailed to the email stored on the account (the address the
   * creator specified).
   */
  direct_password_set_allowed: boolean;
}

export interface CreateOpsUserRequest {
  email: string;
  roles: string[];
  /** Operator-supplied name. Omit or pass null to keep the label derived. */
  display_name?: string | null;
  feishu_identity?: OpsUserFeishuIdentity | null;
  /** Legacy compatibility: GitHub login is no longer the primary ops SSO. */
  github_identity?: OpsUserGithubIdentity | null;
}

export interface CreateOpsUserResponse {
  user: OpsUserRecord;
}

export interface UpdateOpsUserRequest {
  email?: string;
  roles?: string[];
  /** Omit to keep, string to set, null to clear back to a derived label. */
  display_name?: string | null;
  /** Omit to keep existing binding, object to bind/update, null to unbind. */
  feishu_identity?: OpsUserFeishuIdentity | null;
  /** Legacy compatibility: GitHub login is no longer the primary ops SSO. */
  github_identity?: OpsUserGithubIdentity | null;
}

export interface UpdateOpsUserResponse {
  user: OpsUserRecord;
}

/** `POST /internal/ops-users/:id/disable` has an empty body. */
export interface DisableOpsUserResponse {
  user: OpsUserRecord;
}

/** `POST /internal/ops-users/:id/enable` has an empty body. */
export interface EnableOpsUserResponse {
  user: OpsUserRecord;
}

/**
 * `POST /internal/ops-users/:id/reset-password` — deletes the
 * user's `ops_password_credentials` row. Forces an admin re-seed via
 * a fresh INSERT (out-of-band, typically via wrangler / the setup
 * script); there is no user-facing self-service reset flow in phase 1.
 * Also invalidates all live ops sessions for the user so the access
 * cookie can't outlive the credential.
 */
export interface ResetOpsUserPasswordResponse {
  user: OpsUserRecord;
  /** Count of `ops_sessions` rows deleted by the reset. */
  sessions_invalidated: number;
}

/**
 * `POST /internal/ops-users/:id/set-password` — **non-production only**.
 * Admin-set (or self-set) a scrypt credential. Rank-gated: the caller
 * named by `X-Ops-User-Id` must not have a strictly lower privilege
 * than the target. Production refuses this endpoint so an admin cannot
 * type another person's password into the API (router#2032). That is
 * not the same as "cannot mint a second loginable identity": create
 * already accepts a creator-chosen email and optional SSO binding.
 * Replacing an existing credential invalidates live sessions; a
 * first-time set does not.
 */
export interface SetOpsUserPasswordRequest {
  password: string;
}

export interface SetOpsUserPasswordResponse {
  user: OpsUserRecord;
  /** True when an existing credential row was replaced. */
  replaced: boolean;
  /** Count of `ops_sessions` rows deleted. Zero on a first-time set. */
  sessions_invalidated: number;
}

/**
 * `POST /internal/ops-users/:id/invite-password` — all environments.
 * Mails a one-time set-password link to the email stored on the
 * account — the address the creator specified, not a verified
 * identity. The plaintext token is never returned on this response
 * (or the operator-facing proxy). This constrains casually reading
 * another operator's existing password, not minting a second identity
 * the creator controls. `console_origin` is injected by ops-api from
 * `OPS_CONSOLE_ORIGIN`, not taken from the operator body.
 */
export interface InviteOpsUserPasswordRequest {
  console_origin: string;
}

export interface InviteOpsUserPasswordResponse {
  user: OpsUserRecord;
  expires_at: string;
}

/**
 * `POST /internal/ops/consume-password-invite` — unauthenticated.
 * The token holder (the invited operator) sets their own password.
 */
export interface ConsumeOpsUserPasswordInviteRequest {
  token: string;
  password: string;
}

export interface ConsumeOpsUserPasswordInviteResponse {
  ok: true;
}

/** `POST /internal/ops-users/:id/revoke-sessions` has an empty body. */
export interface RevokeOpsUserSessionsResponse {
  user: OpsUserRecord;
  /** Count of `ops_sessions` rows deleted by the revocation. */
  sessions_invalidated: number;
}

// ─── sales profiles (epic #1030 E1-S1, #1037) ────────────────────────────

/**
 * One row per sales user in `sales_profiles`: exactly one brand per
 * ops user. Brand is validated against the shared brand registry
 * (`BRAND_IDS` in `brands.ts`) at the platform-api boundary.
 */
export interface SalesProfileRecord {
  ops_user_id: string;
  brand: BrandId;
  created_at: string;
  updated_at: string;
}

/** `GET /internal/ops-users/:id/sales-profile` — `profile` is null when unset. */
export interface GetSalesProfileResponse {
  profile: SalesProfileRecord | null;
}

/**
 * `PUT /internal/ops-users/:id/sales-profile` — insert-or-update the
 * user's brand. Rejected with `INVALID_PAYLOAD` when `brand` is outside
 * the brand registry, and with `SALES_PROFILE_HAS_ACTIVE_CUSTOMERS`
 * when the brand would CHANGE while the user still owns active
 * customers (fail-closed; transfer the book first).
 */
export interface SetSalesProfileRequest {
  brand: string;
}

export interface SetSalesProfileResponse {
  profile: SalesProfileRecord;
}

/**
 * `DELETE /internal/ops-users/:id/sales-profile` — rejected with
 * `SALES_PROFILE_HAS_ACTIVE_CUSTOMERS` while the user owns active
 * (`revoked_at IS NULL`) `customer_manager_assignments` rows.
 */
export interface DeleteSalesProfileResponse {
  deleted: true;
  ops_user_id: string;
}

// ─── one-click salesperson provisioning ──────────────────────────────────

/**
 * `POST /internal/ops-users/provision-salesperson` — create a salesperson
 * account, its brand profile, its login password and its invite code in ONE
 * transaction, so a half-provisioned operator can never exist.
 *
 * There is deliberately NO `password` field. The server generates it. That
 * is what keeps this endpoint inside router#2032's rule rather than around
 * it: #2032 bans an admin *typing another person's password into the API*,
 * and the ban on `POST /:id/set-password` in production stays exactly as it
 * was. Here the admin cannot influence the secret at all.
 */
export interface ProvisionSalespersonRequest {
  email: string;
  /** Required here, unlike `CreateOpsUserRequest` — the point of the flow. */
  display_name: string;
  /** Validated against the shared brand registry, same as SetSalesProfile. */
  brand: string;
  /**
   * Optional caller-chosen invite-code text. Omitted or null keeps server
   * generation. A string is normalized with `normalizeCustomInviteCode`
   * against `brand`; anything else is `INVITE_CODE_FORMAT_INVALID`. A
   * uniqueness hit rolls the whole provision back (`INVITE_CODE_TAKEN`).
   */
  invite_code?: string | null;
  /** Positive integer; omitted → the invite settings default applies. */
  daily_quota_override?: number;
  /**
   * The minted code's trial-grant amount, in CNY cents (#2022 Part A).
   *
   * Same semantics as `CreateInviteCodeRequest`: the console prefills the
   * recommended value from `GET /internal/invite-codes/defaults` and sends
   * back whatever the operator confirmed, so the code carries an explicit
   * snapshot rather than tracking later edits to the global singleton.
   * Positive integer, and rejected when it exceeds
   * `invite_activation_settings.max_grant_amount_cny_cents`.
   *
   * Omitted stores SQL NULL — the pre-#2022 inherit-at-activation fallback.
   * That path is kept for API callers that only know the three original
   * fields; it is not what the provisioning drawer does.
   */
  grant_amount_cny_cents_override?: number;
  /** Trial-grant validity in days. Same snapshot semantics as the amount. */
  grant_expiry_days_override?: number;
  /**
   * Optional live plan binding for the minted code. Same XOR with
   * `rate_discounts` as `CreateInviteCodeRequest` (this path has no
   * discounts field; a bound plan is the only catalog template).
   */
  model_plan_id?: string;
}

export interface ProvisionSalespersonResponse {
  user: OpsUserRecord;
  sales_profile: SalesProfileRecord;
  invite_code: InviteCode;
  /**
   * The generated login password, in plaintext, returned EXACTLY ONCE.
   *
   * It is never written to `ops_audit` metadata, never logged, and there is
   * no endpoint that can read it back — the stored form is a scrypt hash
   * like every other ops credential. If the operator loses this response the
   * only remedy is `POST /:id/invite-password` (or the salesperson's own
   * self-service change), not a re-read.
   */
  password: string;
}

// ─── self-service password change ────────────────────────────────────────

/**
 * `POST /internal/ops/change-password` — an ops user rotating their OWN
 * password. Knowledge of the current password is the authorization; the
 * caller's identity is pinned by ops-api from the session cookie and is
 * never taken from a client-supplied field.
 *
 * On success every session for the user is destroyed, so the change is not
 * only effective for future logins but actively evicts anyone already
 * holding a cookie.
 */
export interface ChangeOpsPasswordRequest {
  /** Resolved from the ops session by ops-api, not from the browser. */
  ops_user_id: string;
  current_password: string;
  new_password: string;
}

export interface ChangeOpsPasswordResponse {
  ok: true;
  /** Count of `ops_sessions` rows destroyed by the rotation. */
  sessions_invalidated: number;
}
